import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Avatar,
  Chip,
  CircularProgress,
  Typography,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import { colors } from '../../theme/theme';
import SeatMapCanvas, { SeatOccupancy } from './SeatMapCanvas';
import { RoomOrientation, SeatRow, SlotState } from '../../types/seatLayout';

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  LIVE SEAT MAP FOR ONE SESSION
 * ─────────────────────────────────────────────────────────────────────────────
 * Who is on which machine, right now. Reuses the same canvas the room editor
 * draws with — one renderer, so a room can never look different here than it did
 * when it was built.
 *
 * Occupancy is layered ON TOP of the room's own states:
 *
 *   taken   → drawn as out-of-service, because both mean "you cannot have this"
 *   blocked → same, plus a note; broken tonight only
 *   free    → the room's own state (bookable / walkway)
 *
 * The polling is deliberately cheap. `seatChanges` is echoed back as `?since=`,
 * and when nothing has been claimed or freed the server answers from one integer
 * without touching bookings at all.
 *
 * ⚠️ Deliberately shows NO member names. The endpoint returns bare seat ids —
 * it is the same one every member's app hits, and keeping identity out of it by
 * construction means a name cannot leak through carelessness.
 */

interface SessionSeatMapProps {
  sessionId: string;
  /** Poll while the view is open. Off by default — opt in where it's useful. */
  autoRefreshMs?: number;
}

const SessionSeatMap: React.FC<SessionSeatMapProps> = ({
  sessionId,
  autoRefreshMs,
}) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);

  const load = useCallback(
    async () => {
      try {
        // The STAFF map: same shape as the member one plus `occupants`, so the
        // desk can see who holds each machine. It does not support the `since`
        // short-circuit — naming occupants means reading the booking rows either
        // way, so there is nothing to skip.
        const res: any = await apiService.getSessionSeatMapStaff(sessionId);
        const payload = res?.data ?? res;
        setLastChecked(new Date());
        if (payload?.unchanged) return;
        setData(payload);
        setError(null);
      } catch (err: any) {
        setError(
          err?.response?.data?.message || err?.message || 'Could not load the seat map',
        );
      } finally {
        setLoading(false);
      }
    },
    [sessionId],
  );

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  useEffect(() => {
    if (!autoRefreshMs || !data?.hasSeatMap) return;
    const t = setInterval(() => load(), autoRefreshMs);
    return () => clearInterval(t);
  }, [autoRefreshMs, data?.seatChanges, data?.hasSeatMap, load]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error) return <Alert severity="error">{error}</Alert>;

  if (!data?.hasSeatMap) {
    return (
      <Alert severity="info">
        This session has no room, so it books by headcount
        {typeof data?.slots === 'number' ? ` (${data.slots} spaces)` : ''}. Attach a
        room to its class to let members pick a bike.
      </Alert>
    );
  }

  const taken = new Set<string>(data.taken ?? []);
  const blocked = new Set<string>(data.blocked ?? []);

  // The room's own shape, untouched. Occupancy is passed SEPARATELY so a booked
  // machine and a broken one never render the same — an earlier version folded
  // both into `oos` and staff could not tell them apart.
  const rows: SeatRow[] = (data.room?.rows ?? []).map((r: any) => ({
    rowId: r.rowId,
    label: r.label,
    surcharge: r.surcharge ?? 0,
    seats: (r.seats ?? []).map((s: any) => ({
      seatId: s.seatId,
      state: (s.state === '-' ? 'gap' : s.state === 'x' ? 'oos' : 'bookable') as SlotState,
      label: s.number ?? null,
      oosReason: s.oosReason ?? null,
      isAccessible: s.isAccessible,
    })),
  }));

  const occupancy: Record<string, SeatOccupancy> = {};
  taken.forEach((id) => (occupancy[id] = 'booked'));
  // Blocked after booked: if a machine is somehow both, "someone is on it" is
  // the more urgent fact for whoever is reading the room.
  blocked.forEach((id) => {
    if (!occupancy[id]) occupancy[id] = 'blocked';
  });

  const occupants: any[] = data.occupants ?? [];
  // seatId -> name, so hovering a machine names its rider.
  const seatNames: Record<string, string> = {};
  occupants.forEach((o: any) => {
    if (o.user?.name) seatNames[o.seatId] = o.user.name;
  });

  const bookable = data.room?.bookableCount ?? 0;
  const free = Math.max(0, bookable - taken.size - blocked.size);
  // Bookings whose seat the room no longer has. `taken` excludes them (the
  // server reconciles against the live layout), so without this the header
  // would still be a seat short of the people in "Who's riding".
  const orphaned = occupants.filter((o: any) => o.seatMissing).length;

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
        <Typography variant="subtitle1" fontWeight={700}>
          {data.room?.name}
        </Typography>
        <Chip size="small" label={`${taken.size} booked`} color="primary" />
        <Chip size="small" variant="outlined" label={`${free} free`} />
        {orphaned > 0 && (
          <Chip
            size="small"
            color="error"
            label={`${orphaned} without a bike`}
            title="These bookings hold a seat the room no longer has — the room was edited after they booked."
          />
        )}
        {blocked.size > 0 && (
          <Chip
            size="small"
            color="warning"
            variant="outlined"
            label={`${blocked.size} unavailable`}
          />
        )}
        <Box flex={1} />
        <Typography variant="caption" color="text.secondary">
          {lastChecked ? `Updated ${lastChecked.toLocaleTimeString()}` : ''}
        </Typography>
        <Button size="small" startIcon={<Refresh />} onClick={() => load()}>
          Refresh
        </Button>
      </Box>

      <SeatMapCanvas
        rows={rows}
        occupancy={occupancy}
        occupantNames={seatNames}
        frontLabel={data.room?.frontLabel ?? 'INSTRUCTOR'}
        backLabel={data.room?.backLabel ?? 'ENTRANCE'}
        orientation={(data.room?.orientation ?? 'instructor_top') as RoomOrientation}
        readOnly
      />

      <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
        <Key label="Booked" sx={{ background: colors.primaryDark, borderColor: colors.primaryDark }} />
        <Key label="Free" sx={{ background: colors.white, borderColor: colors.divider }} />
        <Key
          label="Out of service"
          sx={{ background: colors.white, borderColor: colors.warning, borderStyle: 'dashed' }}
        />
        <Key label="Unavailable tonight" sx={{ background: colors.greyText3, borderColor: colors.greyText2 }} />
        {/* Surcharge row — hidden. A row surcharge is stored but never charged
            (no partial-payment booking path exists), so a legend entry named a
            price nobody is ever asked to pay. Restore alongside the ring in
            SeatMapCanvas.slotStyles when row pricing is real:
        <Key
          label="Surcharge row"
          sx={{ background: colors.white, borderColor: colors.divider, boxShadow: `inset 0 0 0 2px ${colors.primary}` }}
        /> */}
      </Box>

      {occupants.length > 0 && (
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            Who's riding
          </Typography>
          <Box
            display="grid"
            gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }}
            gap={1}
            mt={0.75}
          >
            {occupants
              .slice()
              .sort((a: any, b: any) => Number(a.seatNumber) - Number(b.seatNumber))
              .map((o: any) => (
                <Box
                  key={o.seatId}
                  display="flex"
                  alignItems="center"
                  gap={1}
                  sx={{
                    p: 0.75,
                    borderRadius: 1.5,
                    // A rider whose bike no longer exists is the one row in
                    // this list that needs acting on, so it does not look like
                    // the others.
                    border: `1px solid ${o.seatMissing ? colors.error : colors.divider}`,
                    background: colors.white,
                  }}
                >
                  <Avatar
                    src={o.user?.image}
                    sx={{ width: 26, height: 26, fontSize: 12, bgcolor: colors.primaryDark }}
                  >
                    {(o.user?.name ?? '?').charAt(0).toUpperCase()}
                  </Avatar>
                  <Box minWidth={0}>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {o.user?.name ?? 'Unknown member'}
                    </Typography>
                    <Typography
                      variant="caption"
                      color={o.seatMissing ? 'error' : 'text.secondary'}
                      noWrap
                    >
                      {o.seatMissing
                        ? `Bike ${o.seatNumber} (row ${o.seatRow}) is no longer in this room — reassign`
                        : `Bike ${o.seatNumber} · row ${o.seatRow}`}
                      {o.user?.phoneNumber ? ` · ${o.user.phoneNumber}` : ''}
                    </Typography>
                  </Box>
                </Box>
              ))}
          </Box>
        </Box>
      )}

      <Box
        sx={{
          fontFamily: 'monospace',
          fontSize: 11,
          color: colors.greyText2,
        }}
      >
        seatChanges {data.seatChanges} · room v{data.room?.version} · capacity{' '}
        {data.slots}
      </Box>
    </Box>
  );
};

const Key: React.FC<{ label: string; sx: any }> = ({ label, sx }) => (
  <Box display="flex" alignItems="center" gap={0.75}>
    <Box sx={{ width: 15, height: 15, borderRadius: 0.75, border: '1.5px solid', ...sx }} />
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
  </Box>
);

export default SessionSeatMap;
